<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoltecPanelHeader from './VoltecPanelHeader.vue';
import IxcLookupAPI from 'dashboard/api/ixcLookup';
import IxcCredentialsAPI from 'dashboard/api/ixcCredentials';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  conversation: { type: Object, default: () => ({}) },
});

const store = useStore();
// Lembrado por atendente como os outros painéis; a chave guarda "recolhido"
// para o painel continuar aberto por padrão.
const { isContactSidebarItemOpen, toggleSidebarUIState } = useUISettings();
const isOpen = computed(
  () => !isContactSidebarItemOpen('is_voltec_actions_collapsed')
);
const credentialsConfigured = ref(false);

// states: loading | no_credentials | search | selecting_client | selecting_contract | ready | status | selecting_invoice | audit
const state = ref('loading');

const audits = ref([]);
const isLoadingAudits = ref(false);

const auditActionLabels = {
  trust_unlock: 'Desbloqueio de confiança',
  unlock_trust: 'Desbloqueio de confiança',
  reset_pppoe: 'Reset PPPoE',
  second_copy: '2ª via do boleto',
  client_status: 'Status do cliente',
  ai_toggle: 'Alternar IA',
  link_client: 'Vincular cliente',
  link_contract: 'Vincular contrato',
  open_os: 'Abrir OS',
};

const auditActionLabel = key => auditActionLabels[key] || key;

const auditTargetSummary = target => {
  if (!target || typeof target !== 'object') return '';
  if (target.contrato_id) return `Contrato #${target.contrato_id}`;
  if (target.cliente_id) return `Cliente #${target.cliente_id}`;
  if (target.invoice_id) return `Boleto #${target.invoice_id}`;
  const entries = Object.entries(target);
  if (!entries.length) return '';
  const [k, v] = entries[0];
  return `${k}: ${v}`;
};

const relativeTime = iso => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return 'há instantes';
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `há ${min} ${min === 1 ? 'minuto' : 'minutos'}`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} ${h === 1 ? 'hora' : 'horas'}`;
  const d = Math.floor(h / 24);
  if (d < 30) return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
  const months = Math.floor(d / 30);
  if (months < 12) return `há ${months} ${months === 1 ? 'mês' : 'meses'}`;
  const years = Math.floor(months / 12);
  return `há ${years} ${years === 1 ? 'ano' : 'anos'}`;
};

const openAuditView = async () => {
  state.value = 'audit';
  isLoadingAudits.value = true;
  audits.value = [];
  try {
    const { data } = await IxcLookupAPI.listAudits(props.conversation.id);
    audits.value = data?.audits || [];
  } catch {
    useAlert('Erro ao carregar histórico.');
  } finally {
    isLoadingAudits.value = false;
  }
};

const searchQuery = ref('');
const isSearching = ref(false);
const searchResults = ref([]);

const selectedClient = ref(null);
const contracts = ref([]);
const isLoadingContracts = ref(false);

const selectedContract = ref(null);
const confirmingAction = ref(null);
const isExecutingAction = ref(false);

const clientStatusData = ref(null);
const isLoadingStatus = ref(false);

const invoices = ref([]);
const isLoadingInvoices = ref(false);
const isSendingBoleto = ref(false);
const showSenha = ref(false);

const aiActiveOverride = ref(null);
const isTogglingAi = ref(false);

const aiActive = computed(() => {
  if (aiActiveOverride.value !== null) return aiActiveOverride.value;
  const v = props.conversation?.additional_attributes?.ai_active;
  return v === undefined ? true : !!v;
});

const toggleAi = async () => {
  if (isTogglingAi.value) return;
  const next = !aiActive.value;
  isTogglingAi.value = true;
  try {
    await IxcLookupAPI.toggleAi({ conversationId: props.conversation.id, active: next });
    aiActiveOverride.value = next;
    useAlert(next ? 'IA ativada para esta conversa.' : 'IA desativada para esta conversa.');
  } catch (e) {
    const body = e?.response?.data?.error || e.message;
    useAlert(`Falha ao alternar IA: ${body}`);
  } finally {
    isTogglingAi.value = false;
  }
};

// ── derived ──────────────────────────────────────────────────────────────────

const ixcClienteId = computed(() => selectedClient.value?.id || props.contact?.custom_attributes?.ixc_cliente_id);
// Ações só no contrato escolhido e conferido na lista do cliente atual: o salvo
// na conversa pode ser de outro cliente se o contato foi religado.
const ixcContratoId = computed(() => selectedContract.value?.id);
const contratoSalvo = computed(
  () => props.conversation?.additional_attributes?.ixc_contrato_id
);

const contractLabel = computed(() => {
  if (!selectedContract.value) return null;
  const c = selectedContract.value;
  return c.descricao || c.login || `Contrato #${c.id}`;
});

const internetStatusLabel = s => {
  if (!s) return null;
  if (s === 'A') return { text: 'Ativo', color: 'text-n-teal-11' };
  if (s === 'D') return { text: 'Desativado', color: 'text-n-ruby-11' };
  if (s === 'CM') return { text: 'Bloqueio manual', color: 'text-n-ruby-11' };
  if (s === 'CA') return { text: 'Bloqueio automático', color: 'text-n-ruby-11' };
  if (s === 'FA') return { text: 'Financeiro em atraso', color: 'text-n-ruby-11' };
  if (s === 'AA') return { text: 'Aguardando assinatura', color: 'text-n-amber-11' };
  return { text: s, color: 'text-n-slate-11' };
};

const statusLabel = computed(() => internetStatusLabel(clientStatusData.value?.contrato?.status_internet));

// ── summary card ─────────────────────────────────────────────────────────────

const BLOQUEIOS = ['CM', 'CA', 'FA'];

const isLoadingSummary = ref(false);

const summaryStatusKey = computed(() => clientStatusData.value?.contrato?.status_internet);

const summaryBadgeDe = s => {
  if (!s) return null;
  if (s === 'A') return { text: 'Ativo', tom: 'bom' };
  if (BLOQUEIOS.includes(s)) return { text: 'Bloqueado', tom: 'ruim' };
  if (s === 'D') return { text: 'Desativado', tom: 'ruim' };
  if (s === 'AA') return { text: internetStatusLabel(s).text, tom: 'atencao' };
  return { text: s, tom: 'neutro' };
};
const summaryBadge = computed(() => summaryBadgeDe(summaryStatusKey.value));

const summaryName = computed(() =>
  clientStatusData.value?.cliente?.razao
    || clientStatusData.value?.cliente?.fantasia
    || selectedClient.value?.razao
    || selectedClient.value?.fantasia
    || ''
);

const summaryContractId = computed(() =>
  clientStatusData.value?.contrato?.id || selectedContract.value?.id || ixcContratoId.value
);

const summaryOverdue = computed(() => Number(clientStatusData.value?.total_em_atraso) || 0);
const summaryOpen = computed(() => Number(clientStatusData.value?.total_em_aberto) || 0);

const summaryPppoe = computed(() => {
  const p = clientStatusData.value?.ultima_conexao_pppoe;
  if (!p) return null;
  const online = !p.stopped_at;
  return {
    online,
    ip: p.framedipaddress || null,
    started_at: p.started_at,
  };
});

// detalhes da conexão (usado na visão "Status do cliente")
const conx = computed(() => clientStatusData.value?.ultima_conexao_pppoe || null);
const conxOnline = computed(() => !!(conx.value && conx.value.started_at && !conx.value.stopped_at));

// ONU / fibra (sinal, temperatura, série)
const onu = computed(() => clientStatusData.value?.onu || null);
const sinalColor = v => {
  const n = Number(v);
  if (Number.isNaN(n)) return 'text-n-slate-12';
  if (n >= -25) return 'text-n-teal-11'; // bom
  if (n >= -27) return 'text-n-amber-11'; // atenção
  return 'text-n-ruby-11'; // crítico
};
const tempColor = v => {
  const n = Number(v);
  if (Number.isNaN(n)) return 'text-n-slate-12';
  if (n >= 65) return 'text-n-ruby-11'; // quente
  if (n >= 55) return 'text-n-amber-11'; // atenção
  return 'text-n-teal-11'; // ok
};

let summaryRequestId = 0;
let refreshTardio = null;
// Cartão e detalhes dividem o mesmo resumo: um pedido novo invalida o anterior,
// e a flag do invalidado não pode ficar girando.
const novoPedidoResumo = () => {
  isLoadingSummary.value = false;
  isLoadingStatus.value = false;
  summaryRequestId += 1;
  return summaryRequestId;
};
// Troca de conversa ou de contrato: a resposta que ainda vem do IXC é do anterior.
const descartarResumoEmAndamento = () => {
  novoPedidoResumo();
  clearTimeout(refreshTardio);
};
const loadSummary = async () => {
  if (!ixcClienteId.value) return;
  const requestId = novoPedidoResumo();
  isLoadingSummary.value = true;
  try {
    const { data } = await IxcLookupAPI.clientStatus({
      clienteId: ixcClienteId.value,
      contratoId: ixcContratoId.value,
    });
    if (requestId !== summaryRequestId) return;
    clientStatusData.value = data;
  } catch (e) {
    // IXC fora: avisa em vez de mostrar o resumo vazio como se não houvesse débito.
    if (requestId === summaryRequestId && e?.response?.status === 502) {
      useAlert('IXC indisponível agora — não foi possível carregar o resumo do cliente.');
    }
  } finally {
    if (requestId === summaryRequestId) isLoadingSummary.value = false;
  }
};

// ── invoice formatting helpers ───────────────────────────────────────────────

const formatBrlValue = value => {
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatBrlDate = isoDate => {
  if (!isoDate || typeof isoDate !== 'string') return isoDate;
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
};

const formatBrDateTime = raw => {
  if (!raw || typeof raw !== 'string') return raw;
  // expects "YYYY-MM-DD HH:MM:SS"
  const [datePart, timePart] = raw.split(' ');
  if (!datePart || !timePart) return raw;
  const [y, m, d] = datePart.split('-');
  const [hh, mm] = timePart.split(':');
  if (!y || !m || !d || !hh || !mm) return raw;
  return `${d}/${m}/${y} ${hh}:${mm}`;
};

const isOverdue = isoDate => {
  if (!isoDate || typeof isoDate !== 'string') return false;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return isoDate < todayStr;
};

const formatBytes = bytes => {
  const n = Number(bytes);
  if (!n || Number.isNaN(n)) return null;
  const gb = n / 1024 / 1024 / 1024;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  return `${(n / 1024 / 1024).toFixed(0)} MB`;
};

// "conectado há Xd Yh Zmin" a partir do início da sessão (hora local do IXC)
const uptimeSince = raw => {
  if (!raw || typeof raw !== 'string') return null;
  const t = new Date(raw.replace(' ', 'T')).getTime();
  if (Number.isNaN(t)) return null;
  let sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const d = Math.floor(sec / 86400);
  sec -= d * 86400;
  const h = Math.floor(sec / 3600);
  sec -= h * 3600;
  const m = Math.floor(sec / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}min`);
  return parts.join(' ');
};

const diaMes = raw => {
  if (!raw) return '';
  const p = String(raw).slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}` : raw;
};

// ── consumo (14 dias) ──────────────────────────────────────────────────────────
const consumoData = ref([]);
const isLoadingConsumo = ref(false);
const consumoMax = computed(() => Math.max(1, ...consumoData.value.map(d => Number(d.download) || 0)));
// classes literais (JIT do Tailwind precisa vê-las inteiras) p/ a barra do gráfico
const BAR_WIDTHS = ['w-0', 'w-1/12', 'w-2/12', 'w-3/12', 'w-4/12', 'w-5/12', 'w-6/12', 'w-7/12', 'w-8/12', 'w-9/12', 'w-10/12', 'w-11/12', 'w-full'];
const barClass = v => BAR_WIDTHS[Math.max(0, Math.min(12, Math.round((Number(v) / consumoMax.value) * 12)))];

const fetchConsumo = async () => {
  isLoadingConsumo.value = true;
  consumoData.value = [];
  state.value = 'consumo';
  try {
    const { data } = await IxcLookupAPI.consumo(ixcContratoId.value);
    consumoData.value = data.consumo || [];
    if (!consumoData.value.length) {
      useAlert('Sem dados de consumo no período.');
      state.value = 'ready';
    }
  } catch {
    useAlert('Erro ao buscar consumo.');
    state.value = 'ready';
  } finally {
    isLoadingConsumo.value = false;
  }
};

// ── abrir OS ───────────────────────────────────────────────────────────────────
const osAssuntos = ref([]);
const osFiliais = ref([]);
const osSetores = ref([]);
const osTicketSetores = ref([]);
const osColaboradores = ref([]);
const isLoadingOsAssuntos = ref(false);
const osAssuntoId = ref('');
const osFilialId = ref('');
const osSetorId = ref('');
const osTicketSetorId = ref('');
const osTecnicoId = ref('');
const osMensagem = ref('');
const isOpeningOs = ref(false);
const osResult = ref(null);

const isResumindo = ref(false);
const gerarResumo = async () => {
  isResumindo.value = true;
  try {
    const { data } = await IxcLookupAPI.osResumo(props.conversation.id);
    if (data.resumo) osMensagem.value = data.resumo;
  } catch {
    // silencioso — o atendente pode escrever a descrição manualmente
  } finally {
    isResumindo.value = false;
  }
};

const startOpenOs = async () => {
  state.value = 'open_os';
  osResult.value = null;
  osAssuntoId.value = '';
  osFilialId.value = '';
  osSetorId.value = '';
  osTicketSetorId.value = '';
  osTecnicoId.value = '';
  osMensagem.value = '';
  gerarResumo(); // pré-preenche a descrição com um resumo de IA da conversa
  if (osAssuntos.value.length) return;
  isLoadingOsAssuntos.value = true;
  try {
    const { data } = await IxcLookupAPI.osAssuntos();
    osAssuntos.value = data.assuntos || [];
    osFiliais.value = data.filiais || [];
    osSetores.value = data.setores || [];
    osTicketSetores.value = data.setores_atendimento || [];
    osColaboradores.value = data.colaboradores || [];
  } catch {
    useAlert('Erro ao carregar opções de OS.');
  } finally {
    isLoadingOsAssuntos.value = false;
  }
};

const submitOs = async () => {
  if (!osFilialId.value || !osAssuntoId.value || !osSetorId.value || !osTicketSetorId.value || !osMensagem.value.trim()) {
    useAlert('Preencha filial, assunto, setores e descrição.');
    return;
  }
  isOpeningOs.value = true;
  try {
    const { data } = await IxcLookupAPI.openOs({
      clienteId: ixcClienteId.value,
      contratoId: ixcContratoId.value,
      assuntoId: osAssuntoId.value,
      mensagem: osMensagem.value.trim(),
      filialId: osFilialId.value,
      osSetorId: osSetorId.value,
      ticketSetorId: osTicketSetorId.value,
      tecnicoId: osTecnicoId.value || undefined,
      conversationId: props.conversation.id,
    });
    osResult.value = data;
    useAlert(data.protocolo ? `OS aberta — protocolo ${data.protocolo}` : 'OS aberta com sucesso.');
  } catch (e) {
    const body = e?.response?.data?.body || e?.response?.data?.error || e.message;
    useAlert(`Falha ao abrir OS: ${body}`);
  } finally {
    isOpeningOs.value = false;
  }
};

const actions = [
  { key: 'second_copy', label: '2ª via + PIX', icon: 'i-lucide-receipt' },
  {
    key: 'unlock_trust',
    label: 'Desbloquear',
    icon: 'i-lucide-lock-open',
    confirmLabel: 'Liberar a internet por confiança?',
    confirmHint: 'O IXC religa o cliente até o pagamento cair; fica no histórico com o seu nome.',
  },
  {
    key: 'reset_pppoe',
    label: 'Reset PPPoE',
    icon: 'i-lucide-refresh-cw',
    confirmLabel: 'Derrubar a sessão PPPoE?',
    confirmHint: 'O cliente fica sem internet até o roteador reconectar.',
  },
  { key: 'open_os', label: 'Abrir OS', icon: 'i-lucide-wrench' },
  { key: 'client_status', label: 'Detalhes técnicos', icon: 'i-lucide-activity' },
  { key: 'change_contract', label: 'Trocar contrato', icon: 'i-lucide-arrow-left-right' },
];

// ── visão geral: semáforo, medidor do sinal, consumo e sugestão ───────────────

const TONS = {
  bom: { caixa: 'bg-n-teal-2 border-n-teal-6', ponto: 'bg-n-teal-9', texto: 'text-n-teal-11' },
  atencao: { caixa: 'bg-n-amber-2 border-n-amber-6', ponto: 'bg-n-amber-9', texto: 'text-n-amber-11' },
  ruim: { caixa: 'bg-n-ruby-2 border-n-ruby-6', ponto: 'bg-n-ruby-9', texto: 'text-n-ruby-11' },
  neutro: { caixa: 'bg-n-alpha-1 border-n-weak', ponto: 'bg-n-slate-8', texto: 'text-n-slate-12' },
};
const ROTULO_SINAL = { bom: 'Bom', atencao: 'Atenção', ruim: 'Crítico' };
// Mesmos limites do sinalColor da visão de detalhes.
const SINAL_BOM = -25;
const SINAL_CRITICO = -27;
const tomDoSinal = rx => {
  if (rx >= SINAL_BOM) return 'bom';
  if (rx >= SINAL_CRITICO) return 'atencao';
  return 'ruim';
};
const fmtDbm = n => `${n.toFixed(1).replace('.', ',').replace('-', '−')} dBm`;

const bloqueado = computed(() => BLOQUEIOS.includes(summaryStatusKey.value));
const sinalRx = computed(() => {
  const n = Number(onu.value?.sinal_rx);
  return onu.value?.sinal_rx && !Number.isNaN(n) ? n : null;
});

// O IXC guarda o nome em maiúsculas.
const PREPOSICOES = ['da', 'das', 'de', 'do', 'dos', 'e'];
const nomeCliente = computed(() =>
  (summaryName.value || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((p, i) => (i > 0 && PREPOSICOES.includes(p) ? p : p[0].toUpperCase() + p.slice(1)))
    .join(' ')
);

const iniciais = computed(() => {
  const partes = nomeCliente.value.split(' ').filter(p => p && !PREPOSICOES.includes(p));
  if (!partes.length) return '?';
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase();
});

// O IXC não tem a velocidade em campo próprio: ela vem no nome do plano
// ("PLANO ESSENCIAL 2026 - 600MB").
const planoDe = contrato => {
  const bruto = contrato?.contrato || '';
  const vel = bruto.match(/(\d+)\s*(GB|GIGA|MB|MEGA|M)\b/i);
  const nome = bruto
    .replace(/^PLANO\s+/i, '')
    .replace(/\b20\d\d\b/, '')
    .replace(/[-–]?\s*\d+\s*(GB|GIGA|MB|MEGA|M)\b.*$/i, '')
    .trim()
    .toLowerCase()
    .replace(/(^|\s)\S/g, l => l.toUpperCase());
  if (!vel) return { nome: nome || bruto, velocidade: null, unidade: '' };
  return { nome, velocidade: vel[1], unidade: /^G/i.test(vel[2]) ? 'Giga' : 'Mega' };
};
const plano = computed(() => planoDe(clientStatusData.value?.contrato));

// Mesma preferência do bridge da IA (syllai-ia/src/services/contrato.ts):
// ativo > ativo bloqueado > aguardando assinatura > o resto. O IXC lista do mais
// novo para o mais antigo, e o mais novo pode ser o desativado (conv 16640).
const PESO_CONTRATO = { A: 0, N: 5, P: 5 };
const PESO_INTERNET = { A: 0, CA: 1, FA: 1, CM: 1, AA: 2 };
const pesoContrato = c =>
  (PESO_CONTRATO[c.status] ?? 10) + (PESO_INTERNET[c.status_internet] ?? 5);
const prestaServico = c => c.status in PESO_CONTRATO;
const SITUACAO_CONTRATO = {
  P: { text: 'Pré-contrato', tom: 'atencao' },
  N: { text: 'Negativado', tom: 'ruim' },
  I: { text: 'Inativo', tom: 'neutro' },
  D: { text: 'Desistiu', tom: 'neutro' },
};

// Boletos de mais de uma casa: cada um com o endereço do contrato dele (o
// endereço próprio do contrato ou, no padrão do cliente, o do cadastro).
const enderecoDoContrato = c => {
  const base =
    c.endereco_padrao_cliente === 'N'
      ? c
      : clientStatusData.value?.cliente || {};
  const rua = [base.endereco, base.numero].filter(Boolean).join(', ');
  return [rua, base.bairro].filter(Boolean).join(' — ');
};
// Cobrança avulsa (parcela de equipamento, taxa) vem sem contrato (vazio ou 0).
const contratoDoBoleto = invoice => {
  const c = String(invoice.id_contrato ?? '').trim();
  return c && c !== '0' ? c : null;
};
const variasCasasNosBoletos = computed(
  () => new Set(invoices.value.map(contratoDoBoleto).filter(Boolean)).size > 1
);
const casaDoBoleto = invoice => {
  if (!variasCasasNosBoletos.value) return '';
  if (!contratoDoBoleto(invoice)) return 'Cobrança avulsa';
  const c = contracts.value.find(
    x => String(x.id) === String(invoice.id_contrato)
  );
  return (c && enderecoDoContrato(c)) || `Contrato #${invoice.id_contrato}`;
};

// Lista de contratos: os com serviço primeiro, com a situação real.
const contratosOrdenados = computed(() =>
  [...contracts.value]
    .sort((a, b) => pesoContrato(a) - pesoContrato(b))
    .map(c => {
      const p = planoDe(c);
      const situacao =
        c.status === 'A'
          ? summaryBadgeDe(c.status_internet) || { text: 'Ativo', tom: 'bom' }
          : SITUACAO_CONTRATO[c.status] || { text: 'Inativo', tom: 'neutro' };
      return {
        contrato: c,
        titulo: [p.nome, p.velocidade && `${p.velocidade} ${p.unidade}`].filter(Boolean).join(' · ') || `Contrato #${c.id}`,
        situacao,
      };
    })
);

const semaforo = computed(() => {
  if (!clientStatusData.value) return [];
  const p = summaryPppoe.value;
  let conexao;
  if (bloqueado.value) conexao = { tom: 'ruim', valor: 'Bloqueada', detalhe: statusLabel.value?.text };
  else if (!p) conexao = { tom: 'neutro', valor: 'Sem sessão', detalhe: 'nenhum registro' };
  else if (p.online) conexao = { tom: 'bom', valor: 'Online', detalhe: `há ${uptimeSince(p.started_at) || '—'}` };
  else {
    const fim = conx.value?.stopped_at || '';
    conexao = { tom: 'ruim', valor: 'Offline', detalhe: `desde ${diaMes(fim)} ${fim.slice(11, 16)}` };
  }
  const rx = sinalRx.value;
  const fibra = rx === null
    ? { tom: 'neutro', valor: 'Sem leitura', detalhe: '' }
    : { tom: tomDoSinal(rx), valor: fmtDbm(rx), detalhe: ROTULO_SINAL[tomDoSinal(rx)] };
  const atrasados = clientStatusData.value.quantidade_em_atraso;
  let financeiro;
  if (summaryOverdue.value > 0) {
    financeiro = { tom: 'ruim', valor: `R$ ${formatBrlValue(summaryOverdue.value)}`, detalhe: `${atrasados} em atraso` };
  } else if (summaryOpen.value > 0) {
    financeiro = { tom: 'bom', valor: 'Em dia', detalhe: `R$ ${formatBrlValue(summaryOpen.value)} em aberto` };
  } else {
    financeiro = { tom: 'bom', valor: 'Em dia', detalhe: 'nada em aberto' };
  }
  return [
    { rotulo: 'Conexão', ...conexao },
    { rotulo: 'Fibra', ...fibra },
    { rotulo: 'Financeiro', ...financeiro },
  ];
});

// Regras fixas (sem IA): instantâneas e nunca inventam. A ordem é a prioridade.
const sugestao = computed(() => {
  if (!clientStatusData.value) return null;
  const rx = sinalRx.value;
  if (summaryStatusKey.value === 'D') {
    return { texto: 'Contrato desativado: confira com o financeiro antes de qualquer ação.', destaque: [] };
  }
  if (bloqueado.value && summaryOverdue.value > 0) {
    return {
      texto: 'Bloqueado por atraso: envie a 2ª via com PIX; se o cliente já pagou ou paga hoje, use o desbloqueio de confiança.',
      destaque: ['second_copy', 'unlock_trust'],
    };
  }
  if (bloqueado.value) {
    return { texto: `${statusLabel.value.text}: confira o motivo no IXC antes de desbloquear.`, destaque: [] };
  }
  if (rx !== null && rx < SINAL_CRITICO) {
    return { texto: 'Sinal da fibra crítico: reset não resolve. Abra uma OS de visita técnica.', destaque: ['open_os'] };
  }
  if (rx !== null && rx < SINAL_BOM) {
    return {
      texto: 'Sinal da fibra no limite: se o cliente relata quedas ou lentidão, abra uma OS de visita técnica.',
      destaque: ['open_os'],
    };
  }
  if (summaryPppoe.value && !summaryPppoe.value.online) {
    return {
      texto: 'Ativo, mas desconectado: peça para conferir se a ONU e o roteador estão ligados; se estiverem, tente o reset PPPoE.',
      destaque: ['reset_pppoe'],
    };
  }
  if (summaryOverdue.value > 0) {
    return {
      texto: `R$ ${formatBrlValue(summaryOverdue.value)} em atraso: aproveite e envie a 2ª via com PIX.`,
      destaque: ['second_copy'],
    };
  }
  return {
    texto: 'Conexão e sinal bons. Se o cliente fala em lentidão, o gargalo costuma ser o Wi-Fi da casa: vale oferecer o Mesh.',
    destaque: [],
  };
});

const tileClasse = key =>
  sugestao.value?.destaque.includes(key)
    ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
    : 'border-n-weak text-n-slate-12 hover:bg-n-alpha-1';

// Medidor meia-lua de −30 a −8 dBm (esquerda → direita).
const pontoGauge = (v, r) => {
  const x = Math.max(-30, Math.min(-8, v));
  const ang = ((180 - ((x + 30) / 22) * 180) * Math.PI) / 180;
  return [100 + r * Math.cos(ang), 100 - r * Math.sin(ang)].map(n => Math.round(n * 10) / 10);
};
const arcoGauge = (de, ate) => {
  const [x1, y1] = pontoGauge(de, 80);
  const [x2, y2] = pontoGauge(ate, 80);
  return `M ${x1} ${y1} A 80 80 0 0 1 ${x2} ${y2}`;
};
const ZONAS_SINAL = [
  { tom: 'ruim', de: -30, ate: SINAL_CRITICO - 0.15, classe: 'stroke-n-ruby-9' },
  { tom: 'atencao', de: SINAL_CRITICO + 0.15, ate: SINAL_BOM - 0.15, classe: 'stroke-n-amber-9' },
  { tom: 'bom', de: SINAL_BOM + 0.15, ate: -8, classe: 'stroke-n-teal-9' },
];
const gauge = computed(() => {
  const rx = sinalRx.value;
  if (rx === null) return null;
  const tom = tomDoSinal(rx);
  const [x, y] = pontoGauge(rx, 62);
  return {
    zonas: ZONAS_SINAL.map(z => ({ d: arcoGauge(z.de, z.ate), classe: z.classe, opacidade: z.tom === tom ? 1 : 0.25 })),
    x,
    y,
    tom,
    valor: fmtDbm(rx),
    rotulo: ROTULO_SINAL[tom],
  };
});

// Consumo resumido no cartão; o dia a dia continua na visão "consumo".
const consumoMini = ref([]);
let consumoMiniContrato = null;
const loadConsumoMini = async () => {
  const id = ixcContratoId.value;
  if (!id || consumoMiniContrato === id) return;
  consumoMiniContrato = id;
  consumoMini.value = [];
  try {
    const { data } = await IxcLookupAPI.consumo(id);
    if (String(ixcContratoId.value) === String(id)) consumoMini.value = data.consumo || [];
  } catch {
    // sem o gráfico; o resto do painel segue
  }
};
const consumoBarras = computed(() => {
  const dias = [...consumoMini.value].sort((a, b) => String(a.data).localeCompare(String(b.data))).slice(-14);
  const max = Math.max(1, ...dias.map(d => Number(d.download) || 0));
  return dias.map((d, i) => {
    const h = Math.max(1.5, ((Number(d.download) || 0) / max) * 46);
    return {
      x: i * 10 + 1,
      y: 48 - h,
      h,
      classe: Number(d.download) ? 'fill-n-teal-9' : 'fill-n-slate-6',
      dica: `${diaMes(d.data)}: ${formatBytes(d.download) || '0'}`,
    };
  });
});
const consumoTotal = computed(() => {
  const gb = consumoMini.value.reduce((s, d) => s + (Number(d.download) || 0), 0) / 1024 ** 3;
  return `${Math.round(gb).toLocaleString('pt-BR')} GB`;
});

const abaClasse = ativa =>
  ativa
    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
    : 'text-n-slate-11 hover:text-n-slate-12';

// ── init ─────────────────────────────────────────────────────────────────────

// Troca de conversa (ou de cliente) no meio de um carregamento: a lista de
// contratos da conversa anterior não pode virar o contrato desta.
let geracao = 0;
const novaGeracao = () => {
  geracao += 1;
  return geracao;
};

const init = async () => {
  const g = novaGeracao();
  descartarResumoEmAndamento();
  state.value = 'loading';
  clientStatusData.value = null;
  confirmingAction.value = null;
  selectedContract.value = null;
  selectedClient.value = null;
  contracts.value = [];
  aiActiveOverride.value = null;
  consumoMini.value = [];
  consumoMiniContrato = null;

  try {
    const { data } = await IxcCredentialsAPI.get();
    credentialsConfigured.value = !!data.configured;
  } catch {
    credentialsConfigured.value = false;
  }
  if (g !== geracao) return;

  if (!credentialsConfigured.value) {
    state.value = 'no_credentials';
    return;
  }

  if (contratoSalvo.value) {
    await loadContractFromId(contratoSalvo.value, g);
    return;
  }

  if (ixcClienteId.value) {
    isLoadingContracts.value = true;
    state.value = 'selecting_contract';
    try {
      const { data } = await IxcLookupAPI.listContracts(ixcClienteId.value);
      if (g !== geracao) return;
      contracts.value = data.contracts || [];
      await escolherUnicoComServico();
    } catch {
      if (g !== geracao) return;
      useAlert('Erro ao carregar contratos.');
      state.value = 'search';
    } finally {
      if (g === geracao) isLoadingContracts.value = false;
    }
    return;
  }

  state.value = 'search';
};

const loadContractFromId = async (contratoId, g) => {
  // Sem cliente vinculado não dá para conferir de quem é o contrato salvo.
  if (!ixcClienteId.value) {
    state.value = 'search';
    return;
  }
  isLoadingContracts.value = true;
  try {
    const { data } = await IxcLookupAPI.listContracts(ixcClienteId.value);
    if (g !== geracao) return;
    contracts.value = data.contracts || [];
  } catch {
    if (g !== geracao) return;
    useAlert('Erro ao carregar contratos.');
    state.value = 'search';
    return;
  } finally {
    if (g === geracao) isLoadingContracts.value = false;
  }
  const found = contracts.value.find(c => String(c.id) === String(contratoId));
  if (found) {
    selectedContract.value = found;
    state.value = 'ready';
    return;
  }
  // O contrato salvo não é do cliente atual (o contato foi religado a outro
  // cliente): desbloquear ou resetar nele mexeria na conexão de outra pessoa.
  state.value = 'selecting_contract';
  await escolherUnicoComServico();
};

// ── search ────────────────────────────────────────────────────────────────────

const search = async () => {
  if (!searchQuery.value.trim()) return;
  isSearching.value = true;
  searchResults.value = [];
  try {
    const { data } = await IxcLookupAPI.searchClients(searchQuery.value.trim());
    searchResults.value = data.clients || [];
    if (searchResults.value.length === 0) useAlert('Nenhum cliente encontrado.');
    else state.value = 'selecting_client';
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao buscar cliente.');
  } finally {
    isSearching.value = false;
  }
};

const selectClient = async client => {
  const g = novaGeracao();
  selectedClient.value = client;
  try {
    await IxcLookupAPI.linkClient({ contactId: props.contact.id, clienteId: client.id });
  } catch { /* best-effort */ }
  if (g !== geracao) return;

  isLoadingContracts.value = true;
  state.value = 'selecting_contract';
  try {
    const { data } = await IxcLookupAPI.listContracts(client.id);
    if (g !== geracao) return;
    contracts.value = data.contracts || [];
    await escolherUnicoComServico();
  } catch {
    if (g === geracao) useAlert('Erro ao carregar contratos.');
  } finally {
    if (g === geracao) isLoadingContracts.value = false;
  }
};

const selectContract = async contract => {
  const g = geracao;
  selectedContract.value = contract;
  try {
    await IxcLookupAPI.linkContract({ conversationId: props.conversation.id, contratoId: contract.id });
  } catch { /* best-effort */ }
  if (g === geracao) state.value = 'ready';
};

// Só um contrato com serviço (os outros inativos ou desistências): não há o que escolher.
async function escolherUnicoComServico() {
  const comServico = contracts.value.filter(prestaServico);
  if (comServico.length === 1) await selectContract(comServico[0]);
}

// ── actions ───────────────────────────────────────────────────────────────────

const startAction = action => {
  if (action.key === 'client_status') { fetchClientStatus(); return; }
  if (action.key === 'consumo')       { fetchConsumo(); return; }
  if (action.key === 'open_os')       { startOpenOs(); return; }
  if (action.key === 'second_copy')   { fetchInvoices(); return; }
  if (action.key === 'change_contract') { changeContract(); return; }
  confirmingAction.value = action;
};

const cancelConfirm = () => { confirmingAction.value = null; };

const fetchClientStatus = async () => {
  const requestId = novoPedidoResumo();
  isLoadingStatus.value = true;
  clientStatusData.value = null;
  state.value = 'status';
  try {
    const { data } = await IxcLookupAPI.clientStatus({
      clienteId: ixcClienteId.value,
      contratoId: ixcContratoId.value,
    });
    if (requestId !== summaryRequestId) return;
    clientStatusData.value = data;
  } catch {
    if (requestId !== summaryRequestId) return;
    useAlert('Erro ao buscar status do cliente.');
    state.value = 'ready';
  } finally {
    if (requestId === summaryRequestId) isLoadingStatus.value = false;
  }
};

const fetchInvoices = async () => {
  isLoadingInvoices.value = true;
  invoices.value = [];
  state.value = 'selecting_invoice';
  try {
    const { data } = await IxcLookupAPI.listInvoices(ixcClienteId.value);
    invoices.value = data.invoices || [];
    if (invoices.value.length === 0) {
      useAlert('Nenhum boleto em aberto encontrado.');
      state.value = 'ready';
    }
  } catch {
    useAlert('Erro ao buscar boletos.');
    state.value = 'ready';
  } finally {
    isLoadingInvoices.value = false;
  }
};

const sendBoleto = async invoice => {
  // A geração do PDF leva segundos: se o atendente trocar de conversa nesse
  // meio-tempo, o boleto (com os dados do cliente) não pode ir para a outra.
  const conversationId = props.conversation.id;
  isSendingBoleto.value = true;
  try {
    const { data } = await IxcLookupAPI.getBoletoPdf({
      invoiceId: invoice.id,
      conversationId,
    });
    if (!data.base64) throw new Error('PDF não retornado pelo IXC');

    const binary = atob(data.base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const filename = data.filename || `boleto_${invoice.id}.pdf`;
    const file = new File([bytes], filename, { type: 'application/pdf' });

    await store.dispatch('createPendingMessageAndSend', {
      conversationId,
      message: '',
      files: [file],
      isPrivate: false,
    });

    useAlert('Boleto enviado para o cliente.');
    if (props.conversation?.id === conversationId) state.value = 'ready';

    // Best-effort PIX send — does not block or affect boleto result
    try {
      const pixResult = await IxcLookupAPI.sendPix({
        invoiceId: invoice.id,
        conversationId,
        invoiceAmount: Math.round(Number(invoice.valor) * 100),
        invoiceDueDate: invoice.data_vencimento,
        clientName: summaryName.value || '',
      });
      if (pixResult?.data?.ok === true) {
        useAlert('PIX enviado com sucesso');
      }
    } catch (pixErr) {
      // eslint-disable-next-line no-console
      console.warn('[VoltecActionsPanel] sendPix failed (non-blocking):', pixErr);
    }
  } catch (e) {
    const body = e?.response?.data?.body || e?.response?.data?.error || e.message;
    useAlert(`Falha ao enviar boleto: ${body}`);
  } finally {
    isSendingBoleto.value = false;
  }
};

const executeAction = async () => {
  const action = confirmingAction.value;
  confirmingAction.value = null;
  isExecutingAction.value = true;

  try {
    if (action.key === 'unlock_trust') {
      await IxcLookupAPI.trustUnlock({ contratoId: selectedContract.value?.id, conversationId: props.conversation.id });
      useAlert('Desbloqueio de confiança realizado com sucesso.');
    } else if (action.key === 'reset_pppoe') {
      await IxcLookupAPI.resetPppoe({ contratoId: selectedContract.value?.id, conversationId: props.conversation.id });
      useAlert('Sessão PPPoE encerrada.');
    }
    // a ação muda o status no IXC — atualiza o resumo (e um refresh tardio pega a propagação)
    loadSummary();
    refreshTardio = setTimeout(loadSummary, 2500);
  } catch (e) {
    const body = e?.response?.data?.body || e?.response?.data?.error || e.message;
    useAlert(`Falha: ${body}`);
  } finally {
    isExecutingAction.value = false;
  }
};

const changeContract = () => {
  descartarResumoEmAndamento();
  consumoMiniContrato = null;
  selectedContract.value = null;
  clientStatusData.value = null;
  state.value = ixcClienteId.value ? 'selecting_contract' : 'search';
};

// ── watchers ─────────────────────────────────────────────────────────────────

watch(
  () => [
    props.conversation?.id,
    props.contact?.custom_attributes?.ixc_cliente_id,
  ],
  ([conversa, cliente], [conversaAntes, clienteAntes]) => {
    if (conversa !== conversaAntes) {
      init();
      return;
    }
    // Cliente do contato trocado por fora (ex.: a IA identificou outro titular);
    // a troca feita pelo próprio painel (selectClient) não reinicia a escolha.
    if (
      cliente &&
      clienteAntes &&
      String(cliente) !== String(clienteAntes) &&
      String(cliente) !== String(selectedClient.value?.id)
    )
      init();
  }
);
watch(state, newState => {
  if (newState === 'ready' && !clientStatusData.value && ixcClienteId.value) {
    loadSummary();
  }
  if (newState === 'ready') loadConsumoMini();
});
onMounted(init);
</script>

<template>
  <div class="conversation--actions">
    <AccordionItem
      title="Ações do cliente"
      :is-open="isOpen"
      @toggle="toggleSidebarUIState('is_voltec_actions_collapsed')"
    >
      <div class="p-3 flex flex-col gap-3">

        <!-- cartão do cliente (fixo no topo quando há cliente vinculado) -->
        <div
          v-if="ixcClienteId && (clientStatusData || isLoadingSummary) && state !== 'loading' && state !== 'no_credentials'"
          class="flex flex-col gap-3"
        >
          <div v-if="isLoadingSummary && !clientStatusData" class="flex flex-col gap-2 animate-pulse">
            <div class="h-10 bg-n-alpha-2 rounded-lg" />
            <div class="h-16 bg-n-alpha-2 rounded-lg" />
          </div>
          <template v-else>
            <div class="flex items-start gap-3">
              <div class="size-10 shrink-0 rounded-full bg-n-teal-3 text-n-teal-11 flex items-center justify-center text-sm font-semibold">
                {{ iniciais }}
              </div>
              <div class="flex-1 min-w-0 flex flex-col gap-1">
                <span class="text-sm font-semibold text-n-slate-12 truncate">{{ nomeCliente || 'Cliente IXC' }}</span>
                <span class="flex items-center gap-2 flex-wrap">
                  <span
                    v-if="summaryBadge"
                    class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    :class="[TONS[summaryBadge.tom].caixa, TONS[summaryBadge.tom].texto]"
                  >
                    <span class="size-1.5 rounded-full" :class="[TONS[summaryBadge.tom].ponto]" />
                    {{ summaryBadge.text }}
                  </span>
                  <span v-if="summaryContractId" class="text-xs text-n-slate-11">Contrato #{{ summaryContractId }}</span>
                </span>
              </div>
              <button
                type="button"
                class="shrink-0 flex text-n-slate-11 hover:text-n-slate-12 disabled:opacity-50"
                :disabled="isLoadingSummary"
                title="Atualizar dados do cliente"
                aria-label="Atualizar dados do cliente"
                @click="loadSummary"
              >
                <span class="size-4 i-lucide-refresh-cw" :class="[{ 'animate-spin': isLoadingSummary }]" />
              </button>
            </div>

            <div v-if="plano.nome || state === 'ready'" class="flex items-end justify-between gap-3">
              <div class="flex flex-col min-w-0">
                <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11 truncate">{{ plano.nome }}</span>
                <span v-if="plano.velocidade" class="text-3xl font-bold leading-none text-n-slate-12">
                  {{ plano.velocidade }}<span class="ms-1 text-sm font-medium text-n-slate-11">{{ plano.unidade }}</span>
                </span>
              </div>
              <button
                v-if="state === 'ready'"
                type="button"
                role="switch"
                :aria-checked="aiActive"
                :disabled="isTogglingAi"
                class="shrink-0 flex items-center gap-2 text-xs font-medium text-n-slate-12 disabled:opacity-50"
                @click="toggleAi"
              >
                <span>{{ aiActive ? 'IA atendendo' : 'IA pausada' }}</span>
                <span class="relative inline-flex h-6 w-10 rounded-full transition-colors" :class="[aiActive ? 'bg-n-teal-9' : 'bg-n-slate-6']">
                  <span class="absolute top-0.5 size-5 rounded-full bg-white shadow transition-all" :class="[aiActive ? 'start-[1.125rem]' : 'start-0.5']" />
                </span>
              </button>
            </div>

            <div v-if="semaforo.length" class="flex flex-col gap-1">
              <div
                v-for="s in semaforo"
                :key="s.rotulo"
                class="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs min-w-0"
                :class="[TONS[s.tom].caixa]"
              >
                <span class="size-2 shrink-0 rounded-full" :class="[TONS[s.tom].ponto]" />
                <span class="w-[4.75rem] shrink-0 text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">{{ s.rotulo }}</span>
                <span class="shrink-0 font-bold" :class="[TONS[s.tom].texto]">{{ s.valor }}</span>
                <span class="ms-auto min-w-0 truncate text-end text-[11px] text-n-slate-11">{{ s.detalhe }}</span>
              </div>
            </div>
          </template>
        </div>

        <!-- not linked notice -->
        <div
          v-else-if="!ixcClienteId && state !== 'loading' && state !== 'no_credentials' && state !== 'search' && state !== 'selecting_client'"
          class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg p-2"
        >
          Cliente não identificado no IXC.
        </div>

        <!-- sugestão, sinal e consumo -->
        <template v-if="(state === 'ready' || state === 'audit') && clientStatusData">
          <div v-if="sugestao" class="flex gap-2 items-start rounded-lg border border-n-weak bg-n-alpha-1 p-2.5">
            <span class="size-4 shrink-0 mt-0.5 i-lucide-lightbulb text-n-teal-11" />
            <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
              <b class="text-n-slate-12">Sugestão:</b> {{ sugestao.texto }}
            </p>
          </div>
          <div v-if="gauge || consumoBarras.length" class="grid grid-cols-2 gap-2">
            <div v-if="gauge" class="rounded-lg border border-n-weak p-2 flex flex-col items-center">
              <span class="self-start text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Sinal da fibra</span>
              <svg role="img" :aria-label="`Sinal da fibra ${gauge.valor}, ${gauge.rotulo}`" viewBox="0 0 200 112" class="w-full max-w-[8.5rem]">
                <path
                  v-for="z in gauge.zonas"
                  :key="z.classe"
                  :d="z.d"
                  fill="none"
                  stroke-width="14"
                  :stroke-opacity="z.opacidade"
                  :class="z.classe"
                />
                <line x1="100" y1="100" :x2="gauge.x" :y2="gauge.y" stroke-width="4" stroke-linecap="round" class="stroke-n-slate-12" />
                <circle cx="100" cy="100" r="7" class="fill-n-slate-12" />
              </svg>
              <span class="text-sm font-bold text-n-slate-12">{{ gauge.valor }}</span>
              <span class="text-[10px] font-semibold uppercase tracking-wide" :class="[TONS[gauge.tom].texto]">{{ gauge.rotulo }}</span>
            </div>
            <button
              v-if="consumoBarras.length"
              type="button"
              class="rounded-lg border border-n-weak p-2 flex flex-col gap-1 text-start hover:bg-n-alpha-1"
              title="Ver consumo dia a dia"
              @click="fetchConsumo"
            >
              <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Consumo · 14 dias</span>
              <span class="text-sm font-bold text-n-slate-12">{{ consumoTotal }}</span>
              <svg role="img" :aria-label="`Consumo diário, total ${consumoTotal}`" viewBox="0 0 140 48" preserveAspectRatio="none" class="w-full h-12">
                <rect v-for="b in consumoBarras" :key="b.x" :x="b.x" :y="b.y" width="8" :height="b.h" rx="1.5" :class="b.classe">
                  <title>{{ b.dica }}</title>
                </rect>
              </svg>
              <span class="w-full flex justify-between text-[10px] text-n-slate-11"><span>14 dias atrás</span><span>hoje</span></span>
            </button>
          </div>
        </template>

        <div v-if="state === 'ready' || state === 'audit'" role="tablist" class="grid grid-cols-2 gap-1 p-1 rounded-lg bg-n-alpha-2">
          <button
            type="button"
            role="tab"
            :aria-selected="state === 'ready'"
            class="h-8 rounded-md text-xs font-medium" :class="[abaClasse(state === 'ready')]"
            @click="state = 'ready'"
          >
            Ações
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="state === 'audit'"
            class="h-8 rounded-md text-xs font-medium" :class="[abaClasse(state === 'audit')]"
            @click="openAuditView"
          >
            Histórico
          </button>
        </div>

        <!-- loading -->
        <div v-if="state === 'loading'" class="flex flex-col gap-2 animate-pulse">
          <div class="h-10 bg-n-alpha-2 rounded-lg" />
          <div class="h-16 bg-n-alpha-2 rounded-lg" />
          <div class="h-10 bg-n-alpha-2 rounded-lg" />
        </div>

        <!-- no credentials -->
        <div v-else-if="state === 'no_credentials'" class="flex gap-2.5 items-start rounded-lg border border-n-weak bg-n-alpha-1 p-3">
          <span class="size-4 shrink-0 mt-0.5 i-lucide-key-round text-n-slate-11" />
          <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
            Configure suas credenciais do IXC no
            <router-link to="/app/accounts/profile/settings" class="font-medium text-n-blue-11 hover:underline">perfil</router-link>
            para liberar as ações.
          </p>
        </div>

        <!-- search -->
        <template v-else-if="state === 'search'">
          <p class="mb-0 text-xs text-n-slate-11">Busque o cliente por CPF/CNPJ, nome ou login PPPoE.</p>
          <div class="flex gap-2">
            <label class="flex-1 min-w-0 flex items-center gap-2 h-9 px-2.5 rounded-lg border border-n-weak bg-n-alpha-1 focus-within:border-n-brand">
              <span class="size-4 shrink-0 i-lucide-search text-n-slate-10" />
              <input
                v-model="searchQuery"
                name="ixc_search"
                type="search"
                placeholder="CPF, login, nome..."
                class="flex-1 min-w-0 !mb-0 !p-0 !h-auto !border-0 !bg-transparent text-sm text-n-slate-12 !outline-none !shadow-none"
                @keyup.enter="search"
              >
            </label>
            <NextButton label="Buscar" solid blue sm :disabled="isSearching" :is-loading="isSearching" @click="search" />
          </div>
        </template>

        <!-- selecting client -->
        <template v-else-if="state === 'selecting_client'">
          <VoltecPanelHeader title="Qual cliente?" @back="state = 'search'" />
          <div class="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
            <button
              v-for="client in searchResults"
              :key="client.id"
              type="button"
              class="flex items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-start hover:bg-n-alpha-1"
              @click="selectClient(client)"
            >
              <span class="size-8 shrink-0 rounded-full bg-n-alpha-2 flex items-center justify-center">
                <span class="size-4 i-lucide-user text-n-slate-11" />
              </span>
              <span class="flex-1 min-w-0 flex flex-col">
                <span class="text-sm font-medium text-n-slate-12 truncate">{{ client.razao || client.fantasia }}</span>
                <span class="text-xs text-n-slate-11 tabular-nums">{{ client.cnpj_cpf }} · #{{ client.id }}</span>
              </span>
              <span class="size-4 shrink-0 i-lucide-chevron-right text-n-slate-10" />
            </button>
          </div>
        </template>

        <!-- selecting contract -->
        <template v-else-if="state === 'selecting_contract'">
          <div class="flex items-center justify-between">
            <span class="text-sm font-semibold text-n-slate-12">Qual contrato?</span>
            <button type="button" class="text-xs text-n-slate-11 hover:text-n-slate-12 underline" @click="state = 'search'">trocar cliente</button>
          </div>
          <div v-if="isLoadingContracts" class="flex flex-col gap-2 animate-pulse">
            <div class="h-12 bg-n-alpha-2 rounded-lg" />
            <div class="h-12 bg-n-alpha-2 rounded-lg" />
          </div>
          <div v-else class="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
            <button
              v-for="item in contratosOrdenados"
              :key="item.contrato.id"
              type="button"
              class="flex items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-start hover:bg-n-alpha-1"
              @click="selectContract(item.contrato)"
            >
              <span class="flex-1 min-w-0 flex flex-col">
                <span class="text-sm font-medium text-n-slate-12 truncate">{{ item.titulo }}</span>
                <span class="text-xs text-n-slate-11">Contrato #{{ item.contrato.id }}</span>
              </span>
              <span
                class="shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                :class="[TONS[item.situacao.tom].caixa, TONS[item.situacao.tom].texto]"
              >
                <span class="size-1.5 rounded-full" :class="[TONS[item.situacao.tom].ponto]" />
                {{ item.situacao.text }}
              </span>
            </button>
            <div v-if="!contracts.length" class="text-xs text-n-slate-11 p-2">Nenhum contrato encontrado.</div>
          </div>
        </template>

        <!-- selecting invoice (2ª via) -->
        <template v-else-if="state === 'selecting_invoice'">
          <VoltecPanelHeader title="Qual boleto enviar?" @back="state = 'ready'" />
          <div v-if="isLoadingInvoices" class="flex flex-col gap-2 animate-pulse">
            <div class="h-12 bg-n-alpha-2 rounded-lg" />
            <div class="h-12 bg-n-alpha-2 rounded-lg" />
          </div>
          <div v-else-if="!invoices.length" class="text-xs text-n-slate-11 rounded-lg bg-n-alpha-1 p-3">
            Nenhum boleto em aberto.
          </div>
          <div v-else class="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
            <button
              v-for="invoice in invoices"
              :key="invoice.id"
              type="button"
              :disabled="isSendingBoleto"
              class="flex items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-start hover:bg-n-alpha-1 disabled:opacity-50"
              @click="sendBoleto(invoice)"
            >
              <span class="size-8 shrink-0 rounded-lg bg-n-alpha-2 flex items-center justify-center">
                <span class="size-4 i-lucide-receipt text-n-slate-11" />
              </span>
              <span class="flex-1 min-w-0 flex flex-col">
                <span class="text-sm font-semibold text-n-slate-12 tabular-nums">R$ {{ formatBrlValue(invoice.valor) }}</span>
                <span class="text-xs text-n-slate-11 tabular-nums">Vence {{ formatBrlDate(invoice.data_vencimento) }} · #{{ invoice.id }}</span>
                <span
                  v-if="casaDoBoleto(invoice)"
                  class="text-xs text-n-slate-11 truncate"
                >
                  {{ casaDoBoleto(invoice) }}
                </span>
              </span>
              <span
                class="shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                :class="isOverdue(invoice.data_vencimento) ? [TONS.ruim.caixa, TONS.ruim.texto] : [TONS.atencao.caixa, TONS.atencao.texto]"
              >
                {{ isOverdue(invoice.data_vencimento) ? 'Em atraso' : 'A vencer' }}
              </span>
            </button>
          </div>
          <div v-if="isSendingBoleto" class="flex items-center gap-2 text-xs text-n-slate-11">
            <span class="size-4 i-lucide-loader-circle animate-spin" />
            Enviando o PDF para o cliente...
          </div>
        </template>

        <!-- status view -->
        <template v-else-if="state === 'status'">
          <VoltecPanelHeader title="Situação do cliente" @back="state = 'ready'">
            <button
              type="button"
              class="flex items-center justify-center rounded-lg size-7 shrink-0 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-50"
              :disabled="isLoadingStatus"
              title="Atualizar"
              aria-label="Atualizar"
              @click="fetchClientStatus"
            >
              <span class="size-4 i-lucide-refresh-cw" :class="[{ 'animate-spin': isLoadingStatus }]" />
            </button>
          </VoltecPanelHeader>
          <div v-if="isLoadingStatus && !clientStatusData" class="flex flex-col gap-2 animate-pulse">
            <div class="h-24 bg-n-alpha-2 rounded-lg" />
            <div class="h-20 bg-n-alpha-2 rounded-lg" />
          </div>
          <div v-else-if="clientStatusData" class="flex flex-col gap-2 text-xs">
            <section class="rounded-lg border border-n-weak p-3 flex flex-col gap-1.5">
              <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Cliente</span>
              <div class="flex justify-between gap-3">
                <span class="text-n-slate-11">Nome</span>
                <span class="font-medium text-n-slate-12 text-end">{{ clientStatusData.cliente?.razao || clientStatusData.cliente?.fantasia }}</span>
              </div>
              <div class="flex justify-between gap-3">
                <span class="text-n-slate-11">CPF/CNPJ</span>
                <span class="text-n-slate-12 tabular-nums">{{ clientStatusData.cliente?.cnpj_cpf }}</span>
              </div>
              <div v-if="clientStatusData.cliente?.fone" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Telefone</span>
                <span class="text-n-slate-12 tabular-nums">{{ clientStatusData.cliente?.fone }}</span>
              </div>
              <div v-if="clientStatusData.cliente?.whatsapp" class="flex justify-between gap-3">
                <span class="text-n-slate-11">WhatsApp</span>
                <span class="text-n-slate-12 tabular-nums">{{ clientStatusData.cliente?.whatsapp }}</span>
              </div>
              <div v-if="clientStatusData.cliente?.alerta" class="mt-1 flex gap-2 rounded-lg border p-2" :class="[TONS.atencao.caixa]">
                <span class="size-4 shrink-0 i-lucide-triangle-alert" :class="[TONS.atencao.texto]" />
                <span class="text-n-slate-12">{{ clientStatusData.cliente.alerta }}</span>
              </div>
            </section>

            <section v-if="clientStatusData.contrato" class="rounded-lg border border-n-weak p-3 flex flex-col gap-1.5">
              <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Contrato</span>
              <div class="flex justify-between gap-3">
                <span class="text-n-slate-11">Plano</span>
                <span class="text-n-slate-12 text-end">{{ contractLabel }}</span>
              </div>
              <div v-if="statusLabel" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Internet</span>
                <span :class="statusLabel.color" class="font-medium">{{ statusLabel.text }}</span>
              </div>
              <div v-if="clientStatusData.contrato.velocidade_down" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Velocidade</span>
                <span class="text-n-slate-12 tabular-nums">↓ {{ clientStatusData.contrato.velocidade_down }} · ↑ {{ clientStatusData.contrato.velocidade_up }}</span>
              </div>
              <div v-if="clientStatusData.contrato.data_ativacao && clientStatusData.contrato.data_ativacao !== '0000-00-00'" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Ativação</span>
                <span class="text-n-slate-12 tabular-nums">{{ formatBrlDate(clientStatusData.contrato.data_ativacao) }}</span>
              </div>
            </section>

            <section v-if="conx" class="rounded-lg border border-n-weak p-3 flex flex-col gap-1.5">
              <div class="flex items-center justify-between gap-2">
                <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Conexão</span>
                <span
                  class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  :class="conxOnline ? [TONS.bom.caixa, TONS.bom.texto] : [TONS.neutro.caixa, 'text-n-slate-11']"
                >
                  <span class="size-1.5 rounded-full" :class="[conxOnline ? TONS.bom.ponto : TONS.neutro.ponto]" />
                  {{ conxOnline ? 'Online' : 'Offline' }}
                </span>
              </div>
              <div v-if="conxOnline && uptimeSince(conx.started_at)" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Conectado há</span>
                <span class="text-n-slate-12">{{ uptimeSince(conx.started_at) }}</span>
              </div>
              <div v-else-if="!conxOnline && conx.started_at" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Última conexão</span>
                <span class="text-n-slate-12 tabular-nums">{{ formatBrDateTime(conx.started_at) }}</span>
              </div>
              <div v-if="conx.login" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Login</span>
                <span class="text-n-slate-12 font-mono">{{ conx.login }}</span>
              </div>
              <div v-if="conx.senha" class="flex justify-between items-center gap-3">
                <span class="text-n-slate-11">Senha</span>
                <span class="text-n-slate-12 font-mono inline-flex items-center gap-1.5">
                  {{ showSenha ? conx.senha : '••••••' }}
                  <button
                    type="button"
                    class="flex text-n-slate-11 hover:text-n-slate-12"
                    :title="showSenha ? 'Ocultar senha' : 'Mostrar senha'"
                    :aria-label="showSenha ? 'Ocultar senha' : 'Mostrar senha'"
                    @click="showSenha = !showSenha"
                  >
                    <span class="size-3.5" :class="showSenha ? 'i-lucide-eye-off' : 'i-lucide-eye'" />
                  </button>
                </span>
              </div>
              <div v-if="conx.framedipaddress" class="flex justify-between gap-3">
                <span class="text-n-slate-11">IP</span>
                <span class="text-n-slate-12 font-mono">{{ conx.framedipaddress }}</span>
              </div>
              <div v-if="conx.framedipv6prefix" class="flex justify-between gap-3">
                <span class="text-n-slate-11 shrink-0">IPv6</span>
                <span class="text-n-slate-12 font-mono text-[10px] break-all text-end">{{ conx.framedipv6prefix }}</span>
              </div>
              <div v-if="formatBytes(conx.download_bytes) || formatBytes(conx.upload_bytes)" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Tráfego da sessão</span>
                <span class="text-n-slate-12 tabular-nums">↓ {{ formatBytes(conx.download_bytes) || '—' }} · ↑ {{ formatBytes(conx.upload_bytes) || '—' }}</span>
              </div>
              <div v-if="!conxOnline && conx.terminate_cause" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Motivo da queda</span>
                <span class="text-n-slate-12 text-end">{{ conx.terminate_cause }}</span>
              </div>
            </section>

            <section v-if="onu && (onu.sinal_rx || onu.onu_numero)" class="rounded-lg border border-n-weak p-3 flex flex-col gap-1.5">
              <div class="flex items-center justify-between gap-2">
                <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">ONU / Fibra</span>
                <span v-if="onu.data_sinal" class="text-n-slate-10 text-[10px] tabular-nums">{{ formatBrDateTime(onu.data_sinal) }}</span>
              </div>
              <div v-if="onu.sinal_rx" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Sinal recebido (RX)</span>
                <span :class="sinalColor(onu.sinal_rx)" class="font-medium tabular-nums">{{ onu.sinal_rx }} dBm</span>
              </div>
              <div v-if="onu.sinal_tx" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Sinal enviado (TX)</span>
                <span class="text-n-slate-12 tabular-nums">{{ onu.sinal_tx }} dBm</span>
              </div>
              <div v-if="onu.temperatura" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Temperatura</span>
                <span :class="tempColor(onu.temperatura)" class="font-medium tabular-nums">{{ onu.temperatura }} °C</span>
              </div>
              <div v-if="onu.mac || onu.onu_numero" class="flex justify-between gap-3">
                <span class="text-n-slate-11 shrink-0">ONU</span>
                <span class="text-n-slate-12 font-mono break-all text-end">
                  {{ onu.mac || ('nº ' + onu.onu_numero) }}
                  <span v-if="onu.mac && onu.onu_numero" class="text-n-slate-10">(nº {{ onu.onu_numero }})</span>
                </span>
              </div>
              <div v-if="onu.causa_ultima_queda" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Última queda</span>
                <span class="text-n-slate-12 text-end">{{ onu.causa_ultima_queda }}</span>
              </div>
            </section>

            <section v-if="clientStatusData.total_em_atraso > 0 || clientStatusData.total_em_aberto > 0" class="rounded-lg border border-n-weak p-3 flex flex-col gap-1.5">
              <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Financeiro</span>
              <div v-if="clientStatusData.total_em_atraso > 0" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Em atraso</span>
                <span class="font-medium tabular-nums" :class="[TONS.ruim.texto]">
                  R$ {{ formatBrlValue(clientStatusData.total_em_atraso) }}
                  <span v-if="clientStatusData.quantidade_em_atraso" class="font-normal text-n-slate-11">· {{ clientStatusData.quantidade_em_atraso }} boletos</span>
                </span>
              </div>
              <div v-if="clientStatusData.total_em_aberto != null" class="flex justify-between gap-3">
                <span class="text-n-slate-11">Em aberto</span>
                <span class="text-n-slate-12 tabular-nums">R$ {{ formatBrlValue(clientStatusData.total_em_aberto) }}</span>
              </div>
            </section>
          </div>
        </template>

        <!-- consumo (14 dias) -->
        <template v-else-if="state === 'consumo'">
          <VoltecPanelHeader title="Consumo dos últimos 14 dias" @back="state = 'ready'" />
          <div v-if="isLoadingConsumo" class="flex flex-col gap-1.5 animate-pulse">
            <div v-for="n in 7" :key="n" class="h-4 bg-n-alpha-2 rounded" />
          </div>
          <div v-else class="flex flex-col gap-1.5 rounded-lg border border-n-weak p-3">
            <div class="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">
              <span>Dia</span>
              <span>Download · Upload</span>
            </div>
            <div
              v-for="d in consumoData"
              :key="d.data"
              class="flex items-center gap-2 text-[11px]"
            >
              <span class="w-9 shrink-0 text-n-slate-11 tabular-nums">{{ diaMes(d.data) }}</span>
              <div class="flex-1 h-2.5 bg-n-alpha-2 rounded-full overflow-hidden">
                <div class="h-full bg-n-teal-9 rounded-full" :class="[barClass(d.download)]" />
              </div>
              <span class="w-28 shrink-0 text-end text-n-slate-12 tabular-nums">
                {{ formatBytes(d.download) || '0' }}
                <span class="text-n-slate-10">· {{ formatBytes(d.upload) || '0' }}</span>
              </span>
            </div>
          </div>
        </template>

        <!-- abrir OS -->
        <template v-else-if="state === 'open_os'">
          <VoltecPanelHeader title="Abrir OS" @back="state = 'ready'" />
          <div v-if="isLoadingOsAssuntos" class="flex flex-col gap-2 animate-pulse">
            <div v-for="n in 5" :key="n" class="h-9 bg-n-alpha-2 rounded-lg" />
          </div>
          <div v-else-if="osResult" class="flex flex-col items-center gap-2 rounded-lg border p-4 text-center" :class="[TONS.bom.caixa]">
            <span class="size-10 rounded-full bg-n-teal-9 flex items-center justify-center">
              <span class="size-5 i-lucide-check text-white" />
            </span>
            <span class="text-sm font-semibold text-n-slate-12">OS aberta no IXC</span>
            <span v-if="osResult.os_id" class="text-xs text-n-slate-11">OS #{{ osResult.os_id }}</span>
            <span v-if="osResult.protocolo" class="text-xs text-n-slate-11">
              Protocolo <span class="font-mono font-medium text-n-slate-12">{{ osResult.protocolo }}</span>
            </span>
          </div>
          <div v-else class="flex flex-col gap-2.5">
            <label class="flex flex-col gap-1">
              <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Filial</span>
              <select v-model="osFilialId" class="!mb-0 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12">
                <option value="" disabled>Selecione...</option>
                <option v-for="f in osFiliais" :key="f.id" :value="f.id">{{ f.nome }}</option>
              </select>
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Assunto</span>
              <select v-model="osAssuntoId" class="!mb-0 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12">
                <option value="" disabled>Selecione...</option>
                <option v-for="a in osAssuntos" :key="a.id" :value="a.id">{{ a.assunto }}</option>
              </select>
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label class="flex flex-col gap-1 min-w-0">
                <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Setor da OS</span>
                <select v-model="osSetorId" class="!mb-0 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12">
                  <option value="" disabled>Selecione...</option>
                  <option v-for="o in osSetores" :key="o.id" :value="o.id">{{ o.nome }}</option>
                </select>
              </label>
              <label class="flex flex-col gap-1 min-w-0">
                <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Setor do atendimento</span>
                <select v-model="osTicketSetorId" class="!mb-0 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12">
                  <option value="" disabled>Selecione...</option>
                  <option v-for="o in osTicketSetores" :key="o.id" :value="o.id">{{ o.nome }}</option>
                </select>
              </label>
            </div>
            <label class="flex flex-col gap-1">
              <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Colaborador responsável</span>
              <select v-model="osTecnicoId" class="!mb-0 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12">
                <option value="">Sem técnico</option>
                <option v-for="tec in osColaboradores" :key="tec.id" :value="tec.id">{{ tec.nome }}</option>
              </select>
            </label>
            <div class="flex flex-col gap-1">
              <div class="flex items-center justify-between">
                <span class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">Descrição</span>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-n-blue-11 hover:bg-n-alpha-2 disabled:opacity-50"
                  :disabled="isResumindo"
                  @click="gerarResumo"
                >
                  <span class="size-3.5 i-lucide-sparkles" :class="[{ 'animate-pulse': isResumindo }]" />
                  {{ isResumindo ? 'Resumindo...' : 'Resumir com IA' }}
                </button>
              </div>
              <textarea
                v-model="osMensagem"
                rows="5"
                :placeholder="isResumindo ? 'Gerando o resumo da conversa...' : 'Descreva o problema ou a solicitação (ou use o resumo da IA).'"
                class="!mb-0 w-full rounded-lg border border-n-weak bg-n-alpha-1 p-2 text-sm text-n-slate-12"
              />
            </div>
            <NextButton label="Abrir OS" solid blue sm :is-loading="isOpeningOs" @click="submitOs" />
          </div>
        </template>

        <!-- histórico de ações -->
        <template v-else-if="state === 'audit'">
          <div v-if="isLoadingAudits" class="text-xs text-n-slate-11">Carregando histórico...</div>
          <div v-else-if="!audits.length" class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg p-3">
            Nenhuma ação registrada ainda.
          </div>
          <ol v-else class="flex flex-col max-h-72 overflow-y-auto mb-0 ps-0 list-none">
            <li v-for="audit in audits" :key="audit.id" class="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-2 pb-3">
              <span
                class="size-7 rounded-full flex items-center justify-center" :class="[audit.result === 'success' ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-ruby-3 text-n-ruby-11']"
              >
                <span class="size-3.5" :class="[audit.result === 'success' ? 'i-lucide-check' : 'i-lucide-x']" />
              </span>
              <div class="flex flex-col gap-0.5 min-w-0 text-xs">
                <span class="font-medium text-n-slate-12">
                  {{ auditActionLabel(audit.action) }}<span v-if="audit.result !== 'success'" class="text-n-ruby-11"> · falhou</span>
                </span>
                <span class="text-n-slate-11 truncate">
                  {{ audit.user_name || 'Sistema' }}<span v-if="auditTargetSummary(audit.target)"> · {{ auditTargetSummary(audit.target) }}</span>
                </span>
                <span class="text-n-slate-10">{{ relativeTime(audit.created_at) }}</span>
              </div>
            </li>
          </ol>
        </template>

        <!-- ações -->
        <template v-else-if="state === 'ready'">
          <div v-if="confirmingAction" class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-3 flex flex-col gap-2">
            <p class="mb-0 text-sm font-semibold text-n-slate-12">{{ confirmingAction.confirmLabel }}</p>
            <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
              {{ confirmingAction.confirmHint }} <span class="font-medium text-n-slate-12">{{ contractLabel }}</span>
            </p>
            <div class="grid grid-cols-2 gap-2 mt-1">
              <NextButton label="Cancelar" outline slate sm @click="cancelConfirm" />
              <NextButton label="Confirmar" solid ruby sm :is-loading="isExecutingAction" @click="executeAction" />
            </div>
          </div>

          <div v-else class="grid grid-cols-3 gap-2">
            <button
              v-for="action in actions"
              :key="action.key"
              type="button"
              class="min-h-[4.25rem] flex flex-col items-center justify-center gap-1.5 rounded-xl border px-1 py-2 text-[11px] font-semibold leading-tight text-center transition-colors" :class="[tileClasse(action.key)]"
              @click="startAction(action)"
            >
              <span class="size-5" :class="[action.icon]" />
              <span>{{ action.label }}</span>
            </button>
          </div>
        </template>

      </div>
    </AccordionItem>
  </div>
</template>
